/**
 * Shared vocabulary and safety semantics for on-device video compression.
 *
 * There are two compressors: a WebCodecs one in the page
 * (utils/prepare_video_for_upload.js) and, in the Android app, the native
 * VideoCompressor plugin (capacitor/video_compress.js), which compresses the
 * app's own copy of a picked video without the page reading it. They compress
 * by different means but to the SAME target (UPLOAD_ENCODE_TARGET below): 720p
 * upload thrift, matching where the server's own transcode ladder ends. The
 * native one used to aim at 11-24 Mbps mezzanine, which barely shrank a phone's
 * own 1080p recording; the server makes 720p of it either way.
 *
 * One deliberate difference: the native compressor converts HDR to SDR
 * (`convertsHdrToSdr`). Divyam's call, 2026-10-20: coaches only ever see SDR,
 * because the server's transcode converts it anyway, and an HDR video left
 * uncompressed is the slowest upload there is. The web compressor keeps the
 * HDR exemption, because WebCodecs cannot tone-map well.
 *
 * What they must NOT differ on is how they describe themselves. A skip reason
 * and a telemetry field name are a contract with analytics, not an
 * implementation detail: if the two compressors spell "already_efficient"
 * differently, or one reports out_bitrate_bps where the other reports
 * out_bitrate, then every question of the form "is compression helping?"
 * silently splits into two half-answers. This module owns that contract.
 *
 * WHAT IS DELIBERATELY NOT HERE: the trigger gate. The native compressor fires
 * on file size (>50MB); the web one fires on PREDICTED SECONDS SAVED (>=10s),
 * a figure it can only compute after demuxing the file — output bitrate times
 * duration, against the device's own measured encode speed and the client's
 * measured uplink. Those are different questions asked at different points in
 * the pipeline, and folding them into one parameterized gate would invent an
 * abstraction over a shape that is not actually shared. Each compressor
 * composes its own gate and borrows the content exemptions below.
 */

import { setForegroundTimeout } from "./foreground_clock";

/**
 * Every skip_reason either compressor may emit. Frozen because these strings
 * are the analytics contract described above — a typo here is a silently
 * missing row in the skip-reason histogram, not a crash.
 *
 * Reasons are grouped by who decides them:
 *   - availability: the compressor cannot run at all
 *   - gate:         the source should not be compressed (per-consumer trigger)
 *   - content:      the source is exempt on its own merits (shared)
 *   - failure:      an attempt was made and did not produce a usable result
 *   - validation:   an output was produced and then rejected (shared)
 *
 * The gate reasons are per-consumer, so not every compressor emits every one:
 * the native bridge gates on file size and emits below_size_threshold, while
 * the web one gates on predicted seconds saved and emits not_worth_it or
 * no_throughput_prior. They live together anyway because a skip-reason
 * histogram is read across both, and a reason that exists in only one file is
 * exactly the kind that gets spelled two ways.
 */
export const SKIP_REASONS = Object.freeze({
  UNAVAILABLE: "unavailable",
  BELOW_SIZE_THRESHOLD: "below_size_threshold",
  // Too large to hold in memory while encoding. Kept distinct from UNAVAILABLE
  // so "this browser cannot compress at all" and "this one file is too big"
  // do not share a bucket — they are the difference between a device problem
  // and a ceiling worth raising, and the large-file companies are the whole
  // reason the feature exists.
  TOO_LARGE: "too_large",
  // The prediction ran and said compressing would not save enough time to be
  // worth the wait. DECIDED BEFORE ANY ENCODING — the three paths that reach it
  // (the seam's cheap size check, a non-positive encode budget, and a predicted
  // saving under the bar) all cost the user nothing but a probe.
  //
  // It used to also cover the two paths BELOW, where an encode started and was
  // then abandoned. That conflation hid the thing worth seeing: on 2026-09-11,
  // 11 uploads at 80MB+ had encoded for 15s to 736s before giving up — ~2 a
  // day, minutes of a client's phone each — and every one of them read in the
  // histogram as a decision we made cheaply and on purpose. Splitting them is
  // what makes "we chose not to" distinguishable from "we tried and lost".
  NOT_WORTH_IT: "not_worth_it",
  // Calibration measured this DEVICE mid-encode, projected the rest of the clip
  // at the measured rate, and the numbers stopped adding up. The design working
  // — it costs a second or two, which is the point of measuring early rather
  // than trusting the prediction to the end. This is the normal way a
  // mispredicted encode ends, and the histogram should show it that way.
  ENCODE_TOO_SLOW: "encode_too_slow",
  // The encode spent its whole budget without ever producing enough frames to
  // measure: an encoder that went quiet, or one so slow that a few frames
  // outlasted the budget between them. Rare, and expensive when it happens —
  // the device has already spent the time and the file uploads at full size
  // anyway. Distinct from ENCODE_TOO_SLOW, which is the same conclusion reached
  // on evidence and reached early, and distinct from TIMEOUT, which is the hard
  // wall-clock ceiling across every rung rather than this file's own budget.
  //
  // It used to absorb both, so a mispredicted encode was filed here after
  // running for a median 46 seconds. If this bucket is not near-empty, a device
  // is producing frames too slowly to be measured at all — look at that, not at
  // the prediction.
  ENCODE_OVERRAN: "encode_overran",
  // No throughput measurement on record, so there is nothing to predict a
  // saving against. Reported separately from not_worth_it because "we never
  // compress because nobody has a prior" and "we compress rarely because it
  // rarely helps" are completely different diagnoses.
  NO_THROUGHPUT_PRIOR: "no_throughput_prior",
  TOO_SHORT: "too_short",
  HDR_EXEMPT: "hdr_exempt_v1",
  HIGH_FPS_EXEMPT: "high_fps_exempt",
  ALREADY_EFFICIENT: "already_efficient",
  PROBE_FAILED: "probe_failed",
  // The encode did not produce a usable file on any rung of the ladder.
  //
  // NOTE ON WHAT THIS DOES AND DOES NOT SEPARATE. A device that refuses to let
  // the encoder scale is permitted behaviour under WebCodecs and lands here
  // alongside encoders that genuinely broke. I tried twice to give the refusal
  // its own name and could not: it throws on the first oversized frame, and so
  // does an encoder that advertised a codec it cannot run. Every signature that
  // separates them in a fake also matches the other in production.
  //
  // It does not need separating to be watched. Before the ImageBitmap fallback
  // was removed this bucket was 9 of 9 that fallback, so it starts empty — any
  // occupancy after that removal IS the population the fallback served, and a
  // count is enough to say whether removing it cost anybody anything.
  COMPRESS_FAILED: "compress_failed",
  TIMEOUT: "timeout",
  NOT_SMALLER: "not_smaller",
  DURATION_MISMATCH: "duration_mismatch",
  ROTATION_LOST: "rotation_lost",
  AUDIO_LOST: "audio_lost",
  // An HDR source came back without its HDR signalling. Same class as
  // rotation_lost: the output is watchable but WRONG, and wrong in a way that
  // degrades gradually enough that nobody files a bug. Only reachable for a
  // compressor that opts into HDR at all; the rest still exempt it up front.
  HDR_LOST: "hdr_lost",
  // Native compressor only (capacitor/video_compress.js). Each is a reason the
  // page fell back to the web compressor, reported as native_failure.
  //
  // The app has no copy of this picked video (it was too big to copy, or the
  // page got the original URI), so there was nothing to hand over natively.
  NOT_LOCATED: "not_located",
  // The phone could not convert this HDR video to SDR (its GPU lacks what
  // Media3's tone-mapper needs). Its own bucket: whether this works across
  // real phones is the open question of the rollout.
  HDR_TONEMAP_FAILED: "hdr_tonemap_failed",
  // The output lost frames (a decoder dropping them). The original is better
  // than a choppy copy; bar-speed clips depend on every frame.
  FRAME_RATE_CHANGED: "frame_rate_changed",
  // Thermal, power-saver or low-battery: the plugin declined to start.
  DEVICE_BAIL: "device_bail",
  // No hardware encoder; the plugin never software-encodes.
  NO_HW_ENCODER: "no_hw_encoder",
  // The plugin was already compressing something it did not get from this
  // page's queue (a page reloaded mid-encode). Left alone rather than handed to
  // the page's compressor, which would read the whole file to do the same job.
  NATIVE_BUSY: "native_busy",
  // The user cancelled the upload while it was being compressed (or before the
  // encode started). Nothing is sent, so this never reaches an upload's record;
  // it exists so a stopped encode is never mistaken for a failure to fall back
  // from, which would start the other compressor on a video nobody wants.
  CANCELLED: "cancelled",
});

/**
 * The output both compressors encode to. Long edge capped at 720p, never
 * upscaled; H.264 at 3.5 Mbps (1.5x above 30 fps), which sits above the
 * server's own 2.5 Mbps so MediaConvert stays the quality bottleneck; and no
 * compression at all above 60 fps, where the frame timing is the content.
 * prepare_video_for_upload.js documents why each number is what it is.
 */
export const UPLOAD_ENCODE_TARGET = Object.freeze({
  maxLongEdge: 1280,
  h264BitrateBps: 3_500_000,
  highFpsThreshold: 30,
  highFpsBitrateMultiplier: 1.5,
  maxFps: 60,
});

/**
 * Sentinel distinguishing "the timeout won the race" from a real result, which
 * is always a plain object and never this symbol.
 */
export const COMPRESSION_TIMEOUT = Symbol("video_compression_timeout");

/**
 * Race a unit of work against a hard timeout. Resolves to either the work's
 * result or the COMPRESSION_TIMEOUT sentinel.
 *
 * `startWork` is a thunk rather than a promise so that a SYNCHRONOUS throw from
 * it is treated exactly like a rejection — an encoder that throws on construct
 * must not escape as a different failure class than one that rejects. When the
 * timeout wins, the work promise is left running with a `.catch` safety net
 * attached up front, so its eventual rejection never surfaces as an unhandled
 * promise rejection.
 *
 * Inside the phone app the timeout counts only time the app is in the
 * foreground. The web compressor does not run while the app is in the
 * background, and the native one stops once the phone suspends the app, so a
 * plain timer would fire the moment the user came back and throw away an
 * encode that had barely started. In a browser it is a plain timeout (see
 * utils/foreground_clock.js).
 *
 * @param {() => Promise<any>} startWork
 * @param {number} timeoutMs
 * @returns {Promise<any|COMPRESSION_TIMEOUT>}
 */
export async function raceWithTimeout(startWork, timeoutMs) {
  let cancelTimeout;
  const timeoutPromise = new Promise((resolve) => {
    cancelTimeout = setForegroundTimeout(
      () => resolve(COMPRESSION_TIMEOUT),
      timeoutMs,
    );
  });

  let workPromise;
  try {
    workPromise = Promise.resolve(startWork());
  } catch (error) {
    cancelTimeout();
    throw error;
  }
  workPromise.catch(() => {});

  try {
    return await Promise.race([workPromise, timeoutPromise]);
  } finally {
    cancelTimeout();
  }
}

/**
 * Measured output bitrate when the encoder reports one, else a size/duration
 * estimate. The estimate exists so telemetry stays populated against an older
 * native build that has not added the field yet; without it, "did the bitrate
 * actually come down?" becomes unanswerable for exactly the clients on stale
 * shells, who are the ones most likely to be having trouble.
 */
export function estimateOutputBitrateBps(out) {
  if (typeof out.bitrateBps === "number") {
    return out.bitrateBps;
  }
  if (
    typeof out.sizeBytes === "number" &&
    typeof out.durationMs === "number" &&
    out.durationMs > 0
  ) {
    return Math.round((out.sizeBytes * 8) / (out.durationMs / 1000));
  }
  return null;
}

/**
 * The flat telemetry object attached to every compression result.
 *
 * Always includes compressed/skip_reason/elapsed_ms; adds src_* once a probe
 * succeeded, and out_* whenever an attempt actually produced an output —
 * INCLUDING an output that then failed validation. Those near-misses are the
 * whole point of the out_* fields: a run that produced a smaller file and threw
 * it away for rotation_lost is the tripwire for "we are quietly degrading
 * quality", and it is invisible if only successes are reported.
 *
 * Never includes a raw file path.
 */
export function buildCompressionTelemetry({
  compressed,
  skipReason,
  startedAt,
  probe,
  out,
}) {
  const telemetry = {
    compressed,
    skip_reason: skipReason,
    elapsed_ms: Date.now() - startedAt,
  };

  if (probe) {
    telemetry.src_w = probe.widthPx;
    telemetry.src_h = probe.heightPx;
    telemetry.src_fps = probe.fps;
    telemetry.src_codec = probe.codec;
    telemetry.src_is_hdr = probe.isHdr;
    telemetry.src_bitrate_bps = probe.bitrateBps;
    telemetry.src_duration_ms = probe.durationMs;
    telemetry.src_has_audio = probe.hasAudio;
    telemetry.src_rotation = probe.rotationDegrees;
  }

  if (out) {
    telemetry.out_w = out.widthPx;
    telemetry.out_h = out.heightPx;
    telemetry.out_fps = out.fps;
    telemetry.out_codec = out.codec;
    telemetry.out_bitrate_bps = estimateOutputBitrateBps(out);
    telemetry.out_size_bytes = out.sizeBytes;
    telemetry.rotation_preserved = probe
      ? out.rotationDegrees === probe.rotationDegrees
      : null;
    // null rather than false when the output was not probed for HDR, so
    // "we did not look" stays distinguishable from "we looked and it was lost".
    telemetry.out_is_hdr = out.isHdr ?? null;
    telemetry.hdr_preserved =
      probe?.isHdr === true && typeof out.isHdr === "boolean"
        ? out.isHdr
        : null;
  }

  return telemetry;
}

/**
 * Post-compress validation, shared verbatim by both compressors.
 *
 * Returns the matching skip reason when the output must be discarded in favour
 * of the original, or null when it is safe to use. Order matters: not_smaller
 * is checked first because an output that is not smaller is worthless whatever
 * else is true of it, and checking it first keeps the histogram honest rather
 * than attributing the discard to whichever secondary defect happened to be
 * present too.
 *
 * rotation_lost is the one that must never be relaxed. Both server transcoders
 * run MediaConvert with rotate: 'AUTO', reading the container display matrix,
 * so dropping that matrix delivers every portrait phone video to the coach
 * rotated 90 degrees — and it is invisible until a human looks at it.
 */
export function validationFailureReason({
  out,
  sizeBytes,
  probe,
  // True for a compressor that converts HDR to SDR on purpose (the native one),
  // so an HDR source coming back SDR is the plan rather than a loss.
  convertsHdrToSdr = false,
}) {
  if (
    !(
      typeof out?.sizeBytes === "number" &&
      out.sizeBytes > 0 &&
      out.sizeBytes < sizeBytes
    )
  ) {
    return SKIP_REASONS.NOT_SMALLER;
  }
  // Tolerance is proportional but floored: a 2% drift on a 5s clip is 100ms,
  // which is below the noise of container duration rounding.
  const durationToleranceMs = Math.max(1000, probe.durationMs * 0.02);
  if (Math.abs(out.durationMs - probe.durationMs) > durationToleranceMs) {
    return SKIP_REASONS.DURATION_MISMATCH;
  }
  if (out.rotationDegrees !== probe.rotationDegrees) {
    return SKIP_REASONS.ROTATION_LOST;
  }
  if (probe.hasAudio && !out.hasAudio) {
    return SKIP_REASONS.AUDIO_LOST;
  }
  // Only meaningful for a compressor that chose to encode an HDR source rather
  // than exempt it. `out.isHdr` undefined means the caller does not probe for
  // it, and an unprobed output must not be failed on an unasked question.
  if (probe.isHdr === true && out.isHdr === false && !convertsHdrToSdr) {
    return SKIP_REASONS.HDR_LOST;
  }
  return null;
}

/**
 * Content-based exemptions: reasons to leave a source alone that depend on the
 * source itself rather than on why the caller wanted to compress it. Shared
 * because they are judgements about the video, not about the transport.
 *
 * `targetBitrateBps` is supplied by the caller rather than computed here — it
 * is the one genuinely divergent number (mezzanine vs upload thrift), and
 * "already efficient" means nothing except relative to the target you were
 * going to encode at.
 *
 * Returns a skip reason or null.
 */
export function contentExemptionSkipReason({
  probe,
  maxFps,
  maxDimension,
  targetBitrateBps,
  hdrCapable = false,
  // The native compressor converts HDR to SDR by decision (see the header),
  // so an HDR source is not exempt from it.
  convertsHdrToSdr = false,
}) {
  // HDR is exempt unless the caller can encode it AS HDR. The danger was never
  // HDR itself, it was tone-mapping to SDR: the colour shifts visibly and the
  // source is retained, so it is a one-way door. A compressor that keeps the
  // signalling intact end to end is not doing that, and proves it by probing
  // its own output (see HDR_LOST) rather than by asserting it.
  //
  // Defaults to false so a caller that has not thought about HDR keeps today's
  // blanket exemption — the native bridge does not pass this.
  if (probe.isHdr === true && !hdrCapable && !convertsHdrToSdr) {
    return SKIP_REASONS.HDR_EXEMPT;
  }
  if (probe.fps > maxFps) {
    return SKIP_REASONS.HIGH_FPS_EXEMPT;
  }
  const longEdge = Math.max(probe.widthPx, probe.heightPx);
  if (longEdge <= maxDimension && probe.bitrateBps <= targetBitrateBps) {
    return SKIP_REASONS.ALREADY_EFFICIENT;
  }
  return null;
}

/**
 * The skip reasons worth revisiting once the upload has actually failed.
 *
 * The distinction is not "which reasons are soft" — it is who the reason was
 * about. These two are judgements about TIME: the prediction weighed an encode
 * against a transfer and the transfer won. Every other reason is a fact about
 * the file (too short, too large, already efficient, HDR, high fps) or about
 * the device (probe_failed, compress_failed, encode_too_slow, timeout), and a
 * fact does not change because the upload failed. Re-asking one of those costs
 * the user a second wait to be told the same thing.
 *
 * A time judgement, though, was made against a cost model that a failure has
 * just invalidated. The model priced the alternative to compressing as "the
 * transfer, at the measured rate". After a terminal failure the real price of
 * not compressing includes the transfer that just died and the one after it.
 *
 * Deliberately NOT here:
 *   - below_size_threshold — the native bridge's size gate. A file too small to
 *     be worth compressing is too small for compression to rescue.
 *   - not_smaller / duration_mismatch / rotation_lost / audio_lost / hdr_lost —
 *     the encode ran and its output was rejected. Running it again is
 *     deterministic: the same output, rejected the same way.
 */
export const ESCALATION_SKIP_REASONS = Object.freeze([
  SKIP_REASONS.NOT_WORTH_IT,
  SKIP_REASONS.NO_THROUGHPUT_PRIOR,
]);

/**
 * Whether a terminal upload failure is worth one more try with the file
 * compressed, given what the compressor decided the first time round.
 *
 * A null telemetry means the compressor never ran at all — no flag, no
 * WebCodecs, the native shell, or a coach feedback video. Each of those is a
 * reason it must not run now either, and they are exactly the cases that write
 * nothing, so "we have no decision on record" and "we are not allowed to
 * compress this" are the same answer here.
 *
 * @param {object|null} telemetry the compression telemetry from the attempt
 *   that just failed
 * @returns {boolean}
 */
export function canEscalateAfterFailure(telemetry) {
  if (!telemetry || telemetry.compressed) {
    return false;
  }
  return ESCALATION_SKIP_REASONS.includes(telemetry.skip_reason);
}
